import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles, Theme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import ImageIcon from "@material-ui/icons/Image";
import SendIcon from "@material-ui/icons/Send";
import { RefObject, useEffect, useRef, useState } from "react";
import { changeAppState, useAppState } from "../../../../../common/appState";
import { appsPublicImg } from "../../../../../common/consts";
import { getFriends } from "../../../../../common/hooks/useRelatedUsers";
import { fetchPost, fetchPostFormData } from "../../../../../common/util/fetch";
import { isAndroid } from "../../../../../common/util/osCheck";
import { allItems } from "../../../../zApps/Layout/Login/MyPage/components/OpenableCards/ItemsCard/items";
import { PresentBox } from "../../../../zApps/Layout/Login/MyPage/components/OpenableCards/ItemsCard/items/ItemClass/PresentBox";
import ShurikenProgress from "../../../Animations/ShurikenProgress";
import { PrimaryButton } from "../../../Button/PrimaryButton";
import { Markdown } from "../../../Markdown";
import {
    replyTargetImageKey,
    ReplyToMessageImage,
    replyToMessageKey,
} from "../../../Markdown/CodeRender";
import { User } from "../../../User/types";
import { validateImage } from "../../../User/UserAvatar/validateImage";
import { getMessagesPerDays } from "../getMessagesPerDay";
import {
    MessagesPerDays,
    SendImageMessageResponse,
    SendMessageResponse,
} from "../types";
import {
    CoinsForPresentTypes,
    OpenAllUnopenedPresents,
} from "./ConversationArea/functions";

const collapseTimeout = 500;

export function MessageField({
    myUserId,
    targetUserId,
    setMessages,
    setCurrentlySentMessageNo,
    setTextareaHeight,
    messagesCountToShow,
    showMessageLimitDialog,
    setSendingMessage,
    globalSubmitting,
    setGlobalSubmitting,
    user,
    isOpenAllButtonShown,
    replyTarget,
    setReplyTarget,
    inputRef,
}: {
    myUserId: number;
    targetUserId: number;
    setMessages: (messages: MessagesPerDays) => void;
    setCurrentlySentMessageNo: (messageId: number) => void;
    setTextareaHeight: (height: number) => void;
    messagesCountToShow: number;
    showMessageLimitDialog: () => boolean;
    setSendingMessage: (text: string, isImage?: boolean) => void;
    globalSubmitting: boolean;
    setGlobalSubmitting: (submitting: boolean) => void;
    user: User;
    isOpenAllButtonShown: boolean;
    replyTarget: string;
    setReplyTarget: (text: string) => void;
    inputRef: RefObject<HTMLInputElement>;
}) {
    const { message, setMessage } = useSavedMessage(myUserId, targetUserId);

    const error =
        message.length > 5000
            ? "Message must be less than or equal to 5000 characters"
            : undefined;

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTimeout(() => {
            const containerHeight = containerRef.current?.clientHeight ?? 0;
            setTextareaHeight(containerHeight);
        }, collapseTimeout); // Wait animation
    }, [message, setTextareaHeight, isOpenAllButtonShown, replyTarget]);

    const isReplyToImage = replyTarget.startsWith(replyTargetImageKey);

    const c = useMessageFieldStyles({ isReplyToImage });

    const [localSubmitting, setLocalSubmitting] = useState(false);
    const setSubmitting = (newSubmitting: boolean) => {
        setGlobalSubmitting(newSubmitting);
        setLocalSubmitting(newSubmitting);
    };

    const [items] = useAppState("items");

    const [relatedUsers] = useAppState("relatedUsers");
    const { friends } = getFriends(relatedUsers);
    const isFriend = friends.some(f => f.userId === targetUserId);

    return (
        <div className={c.container} ref={containerRef}>
            {isOpenAllButtonShown && (
                <PrimaryButton
                    fullWidth
                    style={{
                        maxHeight: 30,
                        marginBottom: 5,
                    }}
                    onClick={async () => {
                        setSubmitting(true);

                        const allPresentItems = allItems.filter(
                            item => item instanceof PresentBox
                        ) as PresentBox[];
                        const coinsForPresentTypes =
                            allPresentItems.reduce<CoinsForPresentTypes>(
                                (acc, val) => {
                                    return {
                                        ...acc,
                                        [val.key]: val.presentCoins,
                                    };
                                },
                                { ["registered_envelope"]: 1 }
                            );

                        await OpenAllUnopenedPresents(
                            targetUserId,
                            user.userId,
                            coinsForPresentTypes,
                            setMessages,
                            setCurrentlySentMessageNo,
                            messagesCountToShow
                        );

                        setSubmitting(false);
                    }}
                    disabled={globalSubmitting}
                >
                    {localSubmitting ? (
                        <ShurikenProgress size={20} />
                    ) : (
                        "Open all presents!"
                    )}
                </PrimaryButton>
            )}

            <ReplyTargetArea
                replyTarget={replyTarget}
                setReplyTarget={setReplyTarget}
                isReplyToImage={isReplyToImage}
            />

            <div className={c.inputContainer}>
                <TextField
                    autoFocus={!isAndroid} // Androidではソフトウェアキーボードが出る問題があるため、AndroidではautoFocusしない
                    placeholder="Enter a message"
                    variant="outlined"
                    value={message}
                    multiline
                    maxRows={6}
                    fullWidth
                    className={c.textField}
                    onChange={ev => {
                        setMessage(ev.target.value || "");
                    }}
                    inputProps={{
                        className: c.input,
                        ref: inputRef,
                    }}
                    helperText={
                        error ? (
                            <div className={c.errorText}>{error}</div>
                        ) : undefined
                    }
                    error={!!error}
                />

                <div className={c.buttonsContainer}>
                    {isFriend && (
                        <CoinButton
                            hasText={!!message}
                            targetUserId={targetUserId}
                            hasRegisteredEnvelope={items.some(
                                item =>
                                    item.item.key === "registered_envelope" &&
                                    item.num > 0
                            )}
                        />
                    )}

                    <ImageUploader
                        hasText={!!message}
                        myUserId={myUserId}
                        targetUserId={targetUserId}
                        setMessages={setMessages}
                        setCurrentlySentMessageNo={setCurrentlySentMessageNo}
                        messagesCountToShow={messagesCountToShow}
                        setSendingMessage={setSendingMessage}
                    />

                    <PrimaryButton
                        className={c.sendButton}
                        disabled={!message.trim() || !!error}
                        onClick={async () => {
                            if (showMessageLimitDialog()) {
                                return;
                            }
                            setMessage("");
                            inputRef.current?.focus();

                            const messageWithReply = makeMessageWithReply(
                                message,
                                replyTarget
                            ).trim();
                            setSendingMessage(messageWithReply);
                            setReplyTarget("");
                            const result = await sendMessage(
                                myUserId,
                                targetUserId,
                                messageWithReply,
                                messagesCountToShow
                            );
                            if ("error" in result) {
                                return;
                            }
                            setMessages(getMessagesPerDays(result.messages));
                            setCurrentlySentMessageNo(
                                Math.max(
                                    ...result.messages.map(m => m.messageNo)
                                )
                            );
                        }}
                    >
                        <SendIcon className={c.sendIcon} />
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );
}
const useMessageFieldStyles = makeStyles<Theme, { isReplyToImage: boolean }>(
    theme => ({
        container: {
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: theme.palette.grey[300],
            padding: 5,
            zIndex: 2,
        },
        input: {
            backgroundColor: "white",
            padding: "17px 14px",
            margin: "-18.5px -14px",
            fontSize: 17,
            lineHeight: 1.3,
        },
        textField: { padding: 0 },
        errorText: { color: theme.palette.secondary.main, fontWeight: "bold" },
        buttonsContainer: {
            display: "flex",
            alignItems: "center",
            height: 55,
        },
        sendButton: {
            maxHeight: 49,
            minHeight: 49,
            maxWidth: 49,
            minWidth: 49,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: 5,
            borderRadius: "50%",
        },
        sendIcon: {
            height: 25,
            width: 25,
            color: "white",
        },
        inputContainer: { display: "flex", alignItems: "flex-end" },
    })
);

const imgHeight = 100;
const imgContainerPaddingTop = 5;
const imgContainerPaddingBottom = 8;

function ReplyTargetArea({
    replyTarget,
    setReplyTarget,
    isReplyToImage,
}: {
    replyTarget: string;
    setReplyTarget: (text: string) => void;
    isReplyToImage: boolean;
}) {
    const c = useReplyTargetAreaStyles({ isReplyToImage });

    const [delayedReplyTarget, setDelayedReplyTarget] = useState("");
    useEffect(() => {
        // Prevent replyTarget from disappearing before Collapse closes
        const timeoutId = setTimeout(() => {
            setDelayedReplyTarget(replyTarget);
        }, collapseTimeout);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [replyTarget]);

    const delayedIsReplyToImage =
        delayedReplyTarget.startsWith(replyTargetImageKey);
    const isImageCleared = replyTarget === "" && delayedIsReplyToImage; // Image was cleared but Collapse is still closing

    return (
        <Collapse in={!!replyTarget} timeout={collapseTimeout}>
            <div className={c.replyContainer}>
                <div className={c.replyMarkdownContainer}>
                    {isReplyToImage || isImageCleared ? (
                        <ReplyToMessageImage
                            value={replyTarget || delayedReplyTarget}
                            style={{
                                margin: "-5px -12px -10px",
                                display: "inline-flex",
                                paddingTop: imgContainerPaddingTop,
                                paddingBottom: imgContainerPaddingBottom,
                                paddingLeft: 8,
                                paddingRight: 8,
                                height:
                                    imgHeight +
                                    imgContainerPaddingTop +
                                    imgContainerPaddingBottom, // Calculate height before image is loaded for Collapse
                            }}
                            imgStyle={{
                                objectPosition: "left",
                                height: imgHeight,
                            }}
                        />
                    ) : (
                        <Markdown
                            source={replyTarget || delayedReplyTarget}
                            noLinkShadow
                            isFontSmaller
                            noParagraphMarginBottom
                        />
                    )}
                </div>
                <div className={c.replyDeleteButtonContainer}>
                    <IconButton
                        className={c.replyDeleteIconButton}
                        onClick={() => {
                            setReplyTarget("");
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </div>
        </Collapse>
    );
}

const useReplyTargetAreaStyles = makeStyles<Theme, { isReplyToImage: boolean }>(
    theme => ({
        replyContainer: {
            width: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            marginBottom: 5,
            paddingRight: 10,
            color: theme.palette.grey[600],
            borderRadius: 5,
            display: "flex",
        },
        replyMarkdownContainer: ({ isReplyToImage }) => ({
            maxHeight: 250,
            overflowY: "auto",
            width: "100%",
            padding: isReplyToImage ? "7px 0 3px 11px" : "8px 0 10px 10px",
        }),
        replyDeleteButtonContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        replyDeleteIconButton: { height: 24, width: 24 },
    })
);

function makeMessageWithReply(message: string, reply: string) {
    if (!reply) {
        return message;
    }

    return `\`\`\`${replyToMessageKey}
${reply}
\`\`\`
${message}`;
}

function ImageUploader({
    hasText,
    myUserId,
    targetUserId,
    setMessages,
    setCurrentlySentMessageNo,
    messagesCountToShow,
    setSendingMessage,
}: {
    hasText: boolean;
    myUserId: number;
    targetUserId: number;
    setMessages: (messages: MessagesPerDays) => void;
    setCurrentlySentMessageNo: (messageId: number) => void;
    messagesCountToShow: number;
    setSendingMessage: (text: string, isImage?: boolean) => void;
}) {
    const c = useImageUploaderStyles({ hasText });
    const imageInputRef = useRef<HTMLInputElement>(null);

    return (
        <>
            <IconButton
                onClick={() => {
                    imageInputRef.current?.click();
                }}
                className={c.imageIconButton}
            >
                <ImageIcon className={c.imageIcon} />
            </IconButton>

            <input
                hidden
                type="file"
                accept="image/*"
                className={c.input}
                ref={imageInputRef}
                onChange={async ({ target }) => {
                    if (!target.files || !target.files[0]) {
                        return;
                    }
                    const targetFile = target.files[0];
                    if (imageInputRef.current) {
                        imageInputRef.current.value = ""; // Reset in order to enable to upload the same file again
                    }

                    const error = validateImage(targetFile);
                    if (error) {
                        alert(error);
                        return;
                    }

                    setSendingMessage("", true);
                    const result = await fetchSendImageMessage(
                        myUserId,
                        targetUserId,
                        targetFile,
                        messagesCountToShow
                    );
                    if ("error" in result) {
                        return;
                    }
                    setMessages(getMessagesPerDays(result.messages));
                    setCurrentlySentMessageNo(
                        Math.max(...result.messages.map(m => m.messageNo))
                    );
                }}
            />
        </>
    );
}
const imageIconSize = 35;
const useImageUploaderStyles = makeStyles<Theme, { hasText: boolean }>(
    theme => ({
        imageIconButton: ({ hasText }) => {
            const size = hasText ? 0 : imageIconSize;
            return {
                maxHeight: imageIconSize,
                minHeight: imageIconSize,
                maxWidth: size,
                minWidth: size,
                margin: hasText ? 0 : "0 3px 0 8px",
                padding: 0,
                transition: "all 300ms",
            };
        },
        imageIcon: ({ hasText }) => {
            const size = hasText ? 0 : imageIconSize;
            return {
                width: size,
                height: imageIconSize,
                color: theme.palette.primary.main,
                backgroundColor: "white",
                borderRadius: 4,
                transition: "all 300ms",
                opacity: hasText ? 0 : 1,
            };
        },
    })
);

function CoinButton({
    hasText,
    targetUserId,
    hasRegisteredEnvelope,
}: {
    hasText: boolean;
    targetUserId: number;
    hasRegisteredEnvelope: boolean;
}) {
    const c = useCoinStyles({ hasText });

    return (
        <IconButton
            onClick={() => {
                if (!hasRegisteredEnvelope) {
                    changeAppState("shopPanelState", {
                        open: true,
                        focusedItemKey: "registered_envelope",
                    });
                    return;
                }

                changeAppState("registeredEnvelopePanelState", {
                    open: true,
                    targetUserId,
                });
            }}
            className={c.coinIconButton}
        >
            <img
                src={`${appsPublicImg}system/coin.png`}
                className={c.coinIcon}
            />
        </IconButton>
    );
}
const coinIconSize = 35;
const useCoinStyles = makeStyles<Theme, { hasText: boolean }>({
    coinIconButton: ({ hasText }) => {
        const size = hasText ? 0 : coinIconSize;
        return {
            maxHeight: coinIconSize,
            minHeight: coinIconSize,
            maxWidth: size,
            minWidth: size,
            margin: hasText ? 0 : "0 0 0 8px",
            padding: 0,
            transition: "all 300ms",
            backgroundColor: "white",
            borderRadius: 4,
            "&:hover": {
                backgroundColor: "white",
            },
        };
    },
    coinIcon: ({ hasText }) => {
        const internalCoinSize = coinIconSize - 5;
        const size = hasText ? 0 : internalCoinSize;
        return {
            width: size,
            height: internalCoinSize,
            transition: "all 300ms",
            opacity: hasText ? 0 : 1,
        };
    },
});

async function fetchSendImageMessage(
    myUserId: number,
    toUserId: number,
    file: File,
    num: number
): Promise<SendImageMessageResponse> {
    const formData = new FormData();
    formData.append("myUserId", myUserId.toString());
    formData.append("toUserId", toUserId.toString());
    formData.append("file", file);
    formData.append("num", num.toString());

    return await fetchPostFormData<SendImageMessageResponse>(
        "api/Message/SendImageMessage",
        formData
    );
}

function useSavedMessage(myUserId: number, targetUserId: number) {
    const storageKey = `Inputting-MessageField-from${myUserId}-to${targetUserId}`;
    const [message, setMessage] = useState(
        localStorage.getItem(storageKey) || ""
    );

    useEffect(() => {
        // Save data
        localStorage.setItem(storageKey, message);
    }, [message, myUserId, targetUserId]);

    return {
        message,
        setMessage,
    };
}

async function sendMessage(
    myUserId: number,
    toUserId: number,
    text: string,
    num: number
): Promise<SendMessageResponse> {
    return fetchPost<
        { myUserId: number; toUserId: number; text: string; num: number },
        SendMessageResponse
    >("api/Message/SendMessage", { myUserId, toUserId, text, num });
}
